/**
 * Spec 027 T017 — `<QrCodeDisplay>`: render a string as a QR code.
 *
 * Wraps `qrcode.react@^4.2.0` (ISC, pure-JS canvas/SVG renderer; no network
 * calls). Used by the InviteTeam step (US 6) at minimum 200×200 px (FR-011).
 * Foreground colour is bound to a design-token (`--color-on-surface`) so the
 * QR contrasts the surface, regardless of light/dark theme.
 */
import { QRCodeSVG } from 'qrcode.react';

export interface QrCodeDisplayProps {
  /** The string to encode (typically the join URL). */
  value: string;
  /** Square side length in CSS pixels. Defaults to 200. */
  size?: number;
  /** Optional accessible label. Defaults to "QR code". */
  ariaLabel?: string;
}

export const QrCodeDisplay: React.FC<QrCodeDisplayProps> = ({
  value,
  size = 200,
  ariaLabel = 'QR code',
}) => {
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{
        background: 'var(--color-neutral-100, #FFFFFF)',
        padding: 'var(--spacing-2, 16px)',
        borderRadius: 'var(--radius-md, 12px)',
        display: 'inline-block',
        lineHeight: 0,
      }}
    >
      {/* Inner SVG inherits role=img from qrcode.react. The outer div above
          carries the accessible label; hide the inner one from the a11y tree
          to avoid double-image announcement and the axe "svg-img-alt" warning. */}
      <span aria-hidden="true">
        <QRCodeSVG
          value={value}
          size={size}
          level="M"
          bgColor="transparent"
          fgColor="var(--color-on-surface, #1D1B20)"
        />
      </span>
    </div>
  );
};

export default QrCodeDisplay;
